import { MbscCalendarEvent } from "@mobiscroll/react";
import { DateTime } from "luxon";
import { t } from "i18next";
import { OccupiedStatus } from "../../FacilityManager/Domain/Types/Status";

/**
 * Merge intervals that overlap or have a gap smaller than gapThreshold.
 * @param intervals - Array of intervals with start and end as DateTime.
 * @param gapThresholdMinutes - Max gap in minutes to still merge.
 * @example schedule A (09:00-10:00), schedule B (10:20-12:00) will be merged.
 * @example schedule A (09:00-10:00), schedule B (10:40-12:00) will not be merged.
 */
export function mergeIntervals(
  intervals: { start: DateTime; end: DateTime }[],
  gapThresholdMinutes: number = 30 // default 30 mins, currently
) {
  if (intervals.length === 0) return [];

  intervals.sort((a, b) => a.start.toMillis() - b.start.toMillis());

  const merged: { start: DateTime; end: DateTime }[] = [];

  for (const interval of intervals) {
    if (merged.length === 0) {
      // First interval → just push it
      merged.push({ start: interval.start, end: interval.end });
      continue;
    }

    const last = merged.at(-1);
    if (!last) continue;
    const gap = interval.start.diff(last.end, "minutes").toObject().minutes ?? 0;

    if (interval.start <= last.end || gap <= gapThresholdMinutes) {
      // Merge overlapping or close intervals
      last.end = DateTime.fromMillis(Math.max(last.end.toMillis(), interval.end.toMillis()));
    } else {
      merged.push({ start: interval.start, end: interval.end });
    }
  }

  return merged;
}

/**
 * show the selected time schedule and all the occupied schedules combined on the one day, regardless of frequence
 */
export function getCombinedSchedule(
  occupiedData: OccupiedStatus[],
  bookingStart: string, // "08:00"
  bookingEnd: string, // "17:00"
  timezone: string
): { occupiedEvents: MbscCalendarEvent[]; selectedEvent: MbscCalendarEvent[] } {
  const tomorrow = DateTime.now().plus({ days: 1 }).startOf("day");

  // 1. Prepare occupied intervals on tomorrow
  const intervals = occupiedData.map(book => {
    const start = DateTime.fromISO(book.startTime, { zone: timezone });
    const end = DateTime.fromISO(book.endTime, { zone: timezone });

    return {
      start: tomorrow.set({ hour: start.hour, minute: start.minute }),
      end: tomorrow.set({ hour: end.hour, minute: end.minute }),
      title: `${book.userInfo.firstName ?? "No"} ${book.userInfo.surname ?? "title"}`.trim()
    };
  });

  // Sort by start time
  intervals.sort((a, b) => a.start.toMillis() - b.start.toMillis());

  // 2. Merge occupied intervals using helper
  const mergedOccupied = mergeIntervals(intervals, 30); // merge if schedules gap between ≤ 30 min

  // 3. Prepare single selected event
  const start = DateTime.fromISO(bookingStart, { zone: timezone });
  const end = DateTime.fromISO(bookingEnd, { zone: timezone });

  // the event which is selected by user
  const selectedEvent: MbscCalendarEvent[] = [
    {
      id: 1000,
      title: t("Booking"),
      start: tomorrow.set({ hour: start.hour, minute: start.minute }).toJSDate(),
      end: tomorrow.set({ hour: end.hour, minute: end.minute }).toJSDate(),
      color: "#009E48",
      editable: true
    }
  ];

  // 4. Map merged occupied to events (red)
  const occupiedEvents: MbscCalendarEvent[] = mergedOccupied.map((item, index) => ({
    id: index + 1,
    title: t("Occupied time"),
    start: item.start.toJSDate(),
    end: item.end.toJSDate(),
    color: "#B20000",
    editable: false
  }));

  // 5. Combine arrays
  return { occupiedEvents, selectedEvent };
}
